import { SubscriptionType } from '../types/misc';
import { subscriptionService } from '../services/community/subscriptionService';
import { CommunicationType } from '../types/communication';

export const useSubscriptionOptions = () => {
  return [SubscriptionType.FREE, SubscriptionType.BASE, SubscriptionType.PRO];
};

const { FREE, BASE, PRO } = SubscriptionType;

const f = (i18nKey: string, tiers: SubscriptionType[], isComingSoon = false) => ({
  i18nKey,
  isComingSoon,
  [FREE]: tiers.includes(FREE),
  [BASE]: tiers.includes(BASE),
  [PRO]:  tiers.includes(PRO),
});

export const subscriptionFeatures = [
  // FREE tier features
  f('sharingBetweenMembers', [FREE, BASE, PRO]),
  f('events',                [FREE, BASE, PRO]),
  f('tasks',                 [FREE, BASE, PRO]),
  f('scores',                [FREE, BASE, PRO]),
  f('costSplittingApp',      [FREE, BASE, PRO]),
  f('memberAdministration',  [FREE, BASE, PRO]),
  f('maxPhotos200',          [FREE, BASE, PRO]),
  // BASE tier features
  f('attendanceInsightAndExport',    [BASE, PRO]),
  f('externalCalendars',            [BASE, PRO]),
  f('basicFinancialAdministration', [BASE, PRO]),
  f('monthlyEmails500',             [BASE, PRO]),
  f('maxPhotos1000',                [BASE, PRO]),
  // PRO tier features
  f('psd2BankConnection', [PRO], true),
  f('savingPots',         [PRO], true),
  f('budgetsPandLBalance',[PRO], true),
  f('monthlyEmails1000',  [PRO], true),
  f('maxPhotos5000',      [PRO]),
];

export const baseSubscriptionPrices = {
  [SubscriptionType.FREE]: 0,
  [SubscriptionType.BASE]: 7.5,
  [SubscriptionType.PRO]: 12.5,
};

export const variableSubscriptionPrices = {
  [SubscriptionType.FREE]: 0,
  [SubscriptionType.BASE]: 0.05,
  [SubscriptionType.PRO]: 0.1,
};

export async function downloadInvoice(invoiceId: string) {
  const blob = await subscriptionService.fetchInvoicePdf(invoiceId);

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice_${invoiceId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function isSubscriptionGreaterThanOrEqual(
  subscription: SubscriptionType,
  requiredSubscription: SubscriptionType,
): boolean {
  const subscriptionOrder: SubscriptionType[] = [
    SubscriptionType.FREE,
    SubscriptionType.BASE,
    SubscriptionType.PRO,
  ];

  return subscriptionOrder.indexOf(subscription) >= subscriptionOrder.indexOf(requiredSubscription);
}

export function isSubscriptionGreaterThan(
  subscription: SubscriptionType,
  requiredSubscription: SubscriptionType,
): boolean {
  const subscriptionOrder: SubscriptionType[] = [
    SubscriptionType.FREE,
    SubscriptionType.BASE,
    SubscriptionType.PRO,
  ];

  return subscriptionOrder.indexOf(subscription) > subscriptionOrder.indexOf(requiredSubscription);
}

export function hasRequiredSubscriptionForCommunication(
  type: CommunicationType,
  subscription: SubscriptionType,
): boolean {
  return (
    type === CommunicationType.POSTCARD ||
    ((type === CommunicationType.EMAIL || type === CommunicationType.NOTIFICATION) &&
      subscription !== SubscriptionType.FREE)
  );
}

export const FREE_MEMBERS_INCLUDED = 25;

/** Minimal entity shape required by shouldShowTrialBanner */
interface TrialBannerEntity {
  trialEndDate?: string | Date | null;
  trialType?: unknown;
  subscription?: { type?: unknown } | null;
  paidByBusiness?: boolean;
}

export function shouldShowTrialBanner(entity: TrialBannerEntity | null | undefined): boolean {
  if (!entity?.trialEndDate || !entity?.trialType) return false;
  if (entity?.subscription?.type) return false;
  if (entity?.paidByBusiness) return false;
  const trialEnd = new Date(entity.trialEndDate as string | Date);
  const daysLeft = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return trialEnd > new Date() && daysLeft < 31;
}

export function normalizeMemberCount(
  usage: number | { memberCount: number; [key: string]: unknown },
): number {
  return typeof usage === 'number' ? usage : usage.memberCount;
}
